/**
 * API client for `GET /api/v1/schools/directory`.
 *
 * Implements WI-32e (FR-058): returns every school in the corpus, including
 * those with no scraped data, suitable for the /browse directory page.
 *
 * Query parameters:
 *   q         — optional substring filter on school name (case-insensitive)
 *   division  — optional comma-separated division slugs (OR-unioned)
 *   sort      — "name" | "division" | "region" (default: "name")
 *   page      — 1-indexed (default: 1)
 *   page_size — 10–200 (default: 50)
 */

export interface DirectorySchool {
  slug: string;
  name: string;
  division: string;
  region: string;
  state: string;
  city: string;
  has_data: boolean;
}

export interface DirectoryResponse {
  total: number;
  page: number;
  page_size: number;
  schools: DirectorySchool[];
}

export interface DirectoryParams {
  q?: string;
  division?: string[];
  sort?: 'name' | 'division' | 'region';
  page?: number;
  page_size?: number;
}

export async function fetchDirectory(
  params: DirectoryParams = {},
  signal?: AbortSignal,
): Promise<DirectoryResponse> {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.set('q', params.q);
  if (params.division && params.division.length > 0) {
    // Backend accepts comma-separated division values (portable form).
    searchParams.set('division', params.division.join(','));
  }
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.page_size != null) searchParams.set('page_size', String(params.page_size));

  const url = `/api/v1/schools/directory${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await fetch(url, signal ? { signal } : undefined);
  if (!response.ok) {
    throw new Error(`Directory fetch failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
}
